/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Pivot, PivotItem, Stack } from '@fluentui/react';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { BasicModalProps } from 'src/core/components/BasicModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentDto, ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { CustomSkillProjectKind } from 'src/core/stores/app/types';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { noopUndefined } from 'src/core/utils/base';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { DeployedModelUrlModalProps } from 'src/modules/modelDeploy/DeployedModelUrlModal';
import { DeploymentTab } from 'src/modules/modelDeploy/DeploymentsTab';
import { RegionsTab } from 'src/modules/modelDeploy/RegionsTab';

export type DeploymentStore = CustomSkillDeploymentStore | CustomSkillDeploymentStore;

export type ModelDeployLayoutProps = Pick<DeployedModelUrlModalProps, 'retrieveCode'> & {
    projectName: string;
    isLoadingResources?: boolean;
    isServiceKeyTypeValid?: boolean;
    deletedDeploymentsIds?: string[];
    projectType: CustomSkillProjectKind;
    isMultiRegionDeploymentEnabled?: boolean;
    submitCode: (deploymentName: string) => string;
    predictUrl: (deploymentName: string) => string;
    isOrchestrationProjectConnectedToLUISOrQna?: boolean;
    onDeleteDeployment?: (deploymentName: string) => void;
    onChangeSelectedResource?: (resourceId: string) => void;
    deploymentsStoreHouse: ResourceStoreHouse<DeploymentStore>;
    regionDeploymentStoreHouse?: ResourceStoreHouse<ResourceAssignmentStore>;
    onRenderDeployModal?: (modalProps: BasicModalProps<object>) => React.ReactNode;
    trainedModelStoreHouse: ResourceStoreHouse<CluTrainedModelStore | CustomTextTrainedModelStore>;
    onAddResourceAssignment?: (assignmentData: ResourceAssignmentDto, tracker: ProgressTrackerStore) => void;
    onEditResourceAssignment?: (assignmentData: ResourceAssignmentDto, oldResourceId: string, tracker: ProgressTrackerStore) => void;
    onRemoveResourceAssignment?: (assignmentData: Partial<ResourceAssignmentStore>, tracker: ProgressTrackerStore) => void;
    onSwapDeployments?: (sourceDeploymentName: string, targetDeploymentName: string, tracker: ProgressTrackerStore) => void;
    onDeploySubmit: (deploymentInfo: {
        modelName: string;
        deploymentName: string;
        tracker: ProgressTrackerStore;
        resources: ResourceAssignmentForRegionSwagger[];
    }) => void;
};

const messages = defineMessages({
    pageTitle: { id: 'modelDeployLayout.pageTitle', defaultMessage: 'Deploying a model' },
    learnMore: { id: 'modelDeployLayout.learnMore', defaultMessage: 'click here.' },
    pageSubtitle: {
        id: 'modelDeployLayout.pageSubtitle',
        defaultMessage: `Choose which model to deploy or get the prediction URL for a deployed model. To view pricing for endpoint hosting and service requests after deployment,`
    },
    pageSubtitle2: {
        id: 'modelDeployLayout.pageSubtitle2',
        defaultMessage: `Deploy your project to different regions by assigning language resources with different regions.`
    },
    testingDeployments: {
        id: 'modelDeployLayout.testingDeployments',
        defaultMessage: 'Testing deployments.'
    },
    deploymentsPivotTitle: {
        id: 'modelDeployLayout.deploymentsPivotTitle',
        defaultMessage: 'Deployments'
    },
    regionsPivotTitle: {
        id: 'modelDeployLayout.regionsPivotTitle',
        defaultMessage: 'Regions'
    }
});

export const ModelDeployLayout = observer((props: ModelDeployLayoutProps) => {
    const {
        projectType,
        isServiceKeyTypeValid = true,
        regionDeploymentStoreHouse = null,
        onAddResourceAssignment = noopUndefined,
        onEditResourceAssignment = noopUndefined,
        onRemoveResourceAssignment = noopUndefined,
        isOrchestrationProjectConnectedToLUISOrQna = false,
        isMultiRegionDeploymentEnabled = FeatureToggles.MultiRegionDeployments
    } = props;
    const { localize } = useLocalization();

    const { rootStore } = useGlobalState();

    const { serviceName } = getPortalFromUrl();

    const handlePivotClick = (type: string) => {
        logPivotClick(
            serviceName,
            'DeployModel',
            type,
            rootStore.currentCluProjectStore?.id ?? rootStore.currentCustomTextProjectStore?.id
        );
    };

    const subtitle = (
        <Stack>
            <LuisText>
                {localize(messages.pageSubtitle)}
                &nbsp;
                <ExternalLink href="https://aka.ms/language-pricing">{localize(messages.learnMore)}</ExternalLink>
            </LuisText>
            <LuisText>{localize(messages.pageSubtitle2)}</LuisText>
        </Stack>
    );

    return (
        <Page size="container-fluid" pageName="DeployModelPage" title={localize(messages.pageTitle)} data-id="DeployModel">
            <Stack verticalFill tokens={{ childrenGap: 5 }}>
                <EditableTitle title={localize(messages.pageTitle)} subtitle={subtitle} />
                {isMultiRegionDeploymentEnabled ? (
                    <Pivot onLinkClick={link => handlePivotClick(link.props.itemKey)}>
                        <PivotItem itemKey={'Deployments'} headerText={localize(messages.deploymentsPivotTitle)}>
                            <DeploymentTab {...props} />
                        </PivotItem>

                        <PivotItem itemKey={'Regions'} headerText={localize(messages.regionsPivotTitle)}>
                            <RegionsTab
                                projectType={projectType}
                                isServiceKeyTypeValid={isServiceKeyTypeValid}
                                onAddResourceAssignment={onAddResourceAssignment}
                                onEditResourceAssignment={onEditResourceAssignment}
                                onRemoveResourceAssignment={onRemoveResourceAssignment}
                                regionDeploymentStoreHouse={regionDeploymentStoreHouse}
                                isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                            />
                        </PivotItem>
                    </Pivot>
                ) : (
                    <DeploymentTab {...props} />
                )}
            </Stack>
        </Page>
    );
});
